import React, { FC, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { type Editor, isNodeSelection, posToDOMRect } from '@tiptap/core';
import { usePortalNode } from '@wix/da-ds/pkg/popper/hooks/usePortalNode';
import throttle from 'lodash/throttle';
import ddt from '@wix/da-ddt';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { getMenuUpdate } from '../../../redux/selectors/editor';

import s from './SelectionBubbleMenu.scss';

export interface SelectionBubbleMenuProps {
  editor: Editor;

  /**
   * Override the selection rect with a node instead, this is used to
   * allow for clicks on things inside the editor to open a menu E.g. Links
   */
  forceNode?: HTMLElement | null;

  /**
   * When to show the menu, usually after something is focused
   *
   * For selection something like !editor.state.selection.empty && editor.isFocused
   */
  open: boolean;

  /**
   * Your menu
   */
  children: React.ReactNode;

  /**
   * The height of menu, used to determine onscreen-ness
   */
  menuHeight: number;

  /**
   * The width of menu, used to determine onscreen-ness
   */
  menuWidth: number;

  /**
   * The offset in pixels from the top of the selection rect
   */
  menuOffset?: number;

  /**
   * The padding to add when moving back onto screen when it goes offscreen
   */
  borderPadding?: number;

  /**
   * Wrapper classname
   */
  className?: string;

  /**
   * The scrollable the editor is within, this is usually not viewport so you generally
   * want this options as we need to account for it when placing the menu.
   */
  scrollContainerRef?: React.RefObject<HTMLElement | null>;

  /**
   * Move the element to below instead of above the selection
   */
  forceBelowSelection?: boolean;

  /**
   * Allow top to overflow
   */
  allowTopOverflow?: boolean;
}

const ContainerPortal = ({ children }) => {
  const portalContainer = usePortalNode('bubblemenu');
  return portalContainer && ReactDOM.createPortal(children, portalContainer);
};

/**
 * A very specific implementation of a bubblemenu, that floats near a selection range.
 *
 * Now for mobile this does not work at all because selecting text is hard on mobile
 * and mobile has native menus that get in the way. So in general don't use this for
 * mobile unless it to position a popover near a element in the editor.
 *
 * Use forceNode to pass in the element to use for the rect instead of the selection.
 *
 * Most implementations, including the tiptap one, use tooltip libraries to position
 * this stuff. However, to me that didn't work well. Those libs are designed to
 * show elements next to another element and IMO the problem inside editors is
 * actually different.
 *
 * For example, those impls. do not handle selecting text and then scrolling which is
 * a big deal when embedding images, you want the menu to follow the user and not force
 * then to scroll back up.
 *
 * As this bad UX always irked me with ricos and I couldn't ever fix it I decided to
 * just implement a very specific impl. for our uses.
 *
 * == Editor inside a scrollable
 *
 * For this usecase we require you to provide a ref of the scrollable area, we then
 * keep the menu inside the top and bottom of the scrollable.
 *
 * == Editor inside window
 *
 * For this usecase we will use viewport as the scrollable and keep the menu inside
 * the top of viewport.
 *
 * == Both
 *
 * For both we don't worry about off bottom of off left, and for right we will move
 * the menu back inside the viewport. We ignore the scrollable here too as we are
 * using portal we only check right viewport (I think this is good enough).
 *
 */
const SelectionBubbleMenu: FC<SelectionBubbleMenuProps> = ({
  editor,
  open,
  className,
  children,
  scrollContainerRef,
  menuOffset = 8,
  menuHeight,
  menuWidth,
  borderPadding = 8,
  forceNode,
  forceBelowSelection = false,
  allowTopOverflow = true,
}) => {
  const menuUp = useSelector(getMenuUpdate); // used for externals to trigger updates
  const [winScrollPos, setWinScrollPos] = useState<any>({
    y: window.scrollY,
    x: window.scrollX,
  });
  const [innerScrollPos, setInnerScrollPos] = useState<any>({ y: 0, x: 0 });
  const [menuStyles, setMenuStyles] = useState<React.CSSProperties>({});

  // Track the window position
  useEffect(() => {
    const scroller = window;
    const handleScroll = throttle(() => {
      setWinScrollPos({ y: scroller?.scrollY, x: scroller?.scrollX });
    }, 200);
    if (scroller) {
      scroller.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => scroller?.removeEventListener('scroll', handleScroll);
  }, [open]);

  // Force updates when a non page scroller scrolls, we need to update
  // the menu position on scroll / we don't actually use the value.
  useEffect(() => {
    const scroller = scrollContainerRef?.current;
    const handleScroll = throttle(() => {
      setInnerScrollPos({ y: scroller?.scrollTop, x: scroller?.scrollLeft });
    }, 200);
    if (scroller) {
      scroller.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => scroller?.removeEventListener('scroll', handleScroll);
  }, [scrollContainerRef, open]);

  useEffect(() => {
    if (open) {
      const { ranges } = editor.state.selection;
      const from = Math.min(...ranges.map(range => range.$from.pos));
      const to = Math.max(...ranges.map(range => range.$to.pos));

      if (!(editor.view as any).docView) {
        return;
      }

      let rect;
      try {
        if (forceNode) {
          // Node from external source
          rect = forceNode.getBoundingClientRect();
        } else {
          // Single node selected, rect from node (there is no selection)
          if (isNodeSelection(editor.state.selection)) {
            const node = editor.view.nodeDOM(from);

            if (node instanceof HTMLElement) {
              // Dont show on extensions
              if ((node as HTMLElement)?.classList.contains('react-renderer')) {
                rect = undefined;
              } else {
                rect = node.getBoundingClientRect();
              }
            }
          } else {
            // Rect from selection rect
            rect = posToDOMRect(editor.view, from, to);
          }
        }
      } catch (e) {
        ddt.error('editor', e);
      }
      if (rect) {
        let newTop = rect.top + winScrollPos.y - menuOffset - menuHeight;

        if (forceBelowSelection) {
          newTop = newTop + rect.height + menuHeight + menuOffset + 5;
        }

        let newLeft = rect.left + winScrollPos.x;
        let newRight: string | undefined = undefined;

        // off right of screen we move it back inside
        if (newLeft + menuWidth > (visualViewport?.width ?? Infinity)) {
          newLeft = 'auto';
          newRight = `${borderPadding}px`;
        }

        // off top of scrollarea

        // With ref we keep within the scrollable not the screen
        const scrollableRefRect =
          scrollContainerRef?.current?.getBoundingClientRect();
        if (scrollableRefRect) {
          // the selection rect top is outside the scrollarea and will be floating
          // in the page, we bring it back into the scrollable area and account for
          // viewport scroll pos.
          if (!allowTopOverflow) {
            if (rect.top - menuHeight < scrollableRefRect.top) {
              newTop = scrollableRefRect.top + winScrollPos.y + borderPadding;
            }
          }

          // Also keep the menu inside the bottom of the scrollable
          if (newTop > scrollableRefRect.bottom + winScrollPos.y) {
            newTop =
              scrollableRefRect.bottom -
              (menuHeight + borderPadding) +
              winScrollPos.y;
          }
        }

        // Without ref, we keep within the screen
        if (!scrollContainerRef?.current) {
          if (rect.top - menuHeight < 0) {
            newTop = winScrollPos.y + borderPadding;
          }
        }

        const calcStyles: React.CSSProperties = {
          top: `${newTop}px`,
          left: `${newLeft}px`,
          position: 'absolute',
          right: newRight,
          display: 'block',
        };
        ddt.log('editormenu', 'Menu Position', calcStyles, rect);

        setMenuStyles(calcStyles);

        /*
        Left here because it can be useful to visualise the rect :)
        const debugRect = document.getElementById('debug-rect');
        const debug = debugRect ?? document.createElement('div');
        if (!debugRect) {
          document.body.appendChild(debug);
        }

        const styles: React.CSSProperties = {
          position: 'absolute',
          width: `${rect.width}px`,
          height: `${rect.height}px`,
          left: `${rect.left + window.scrollX}px`,
          top: `${rect.top + window.scrollY}px`,
        };

        debug.setAttribute('id', 'debug-rect');
        debug.style.position = 'absolute';
        debug.style.backgroundColor = '#ff0000aa';
        debug.style.width = `${rect.width}px`;
        debug.style.height = `${rect.height}px`;
        debug.style.left = styles.left as any;
        debug.style.top = styles.top as any;
        debug.style.zIndex = '999999';
        debug.style.minWidth = '10px';
        debug.style.pointerEvents = 'none';*/
      } else {
        const calcStyles: React.CSSProperties = {
          display: 'none',
        };
        setMenuStyles(calcStyles);
      }
    }
  }, [
    editor.state.selection,
    editor.view,
    open,
    winScrollPos,
    innerScrollPos,
    menuOffset,
    menuHeight,
    scrollContainerRef,
    menuWidth,
    borderPadding,
    forceNode,
    forceBelowSelection,
    menuUp,
    allowTopOverflow,
  ]);

  if (!open) {
    return null;
  }

  return (
    <ContainerPortal>
      <div
        className={cn(s['wrapper'], s['fade-in'], className)}
        style={menuStyles}
      >
        {children}
      </div>
    </ContainerPortal>
  );
};
SelectionBubbleMenu.displayName = 'SelectionBubbleMenu';

export default SelectionBubbleMenu;
